"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import GridClearing from "@/components/GridClearing";
import GridRowCenter from "./GridRowCenter";
import { useRefGridMode } from "./RefGridContext";

function Pill({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="group inline-flex items-center gap-2 rounded-full border border-brand-600/30 bg-brand-600/[0.06] px-[18px] py-2.5 text-[14.5px] font-semibold text-brand-600 transition-colors hover:bg-brand-600/10"
    >
      {children}
      <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
    </a>
  );
}

/**
 * Slim "learn more" pill linking a homepage highlight to its dedicated page.
 * Composed at the page level (not baked into shared sections, which appear on
 * other pages too). The negative top margin pulls it up under the preceding
 * section so it reads as belonging to it rather than floating in the band.
 * In grid mode the pill clears its own cells like any other content block.
 */
export default function SectionCta({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  const onGrid = useRefGridMode();
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={rootRef} className="relative -mt-[70px]">
      {onGrid && <GridClearing rootRef={rootRef} />}
      {/* Row-snapped so the pill sits vertically centered in a grid row. */}
      <GridRowCenter className="flex items-center justify-center">
        <div data-grid-block className="relative z-10 w-fit">
          <Pill href={href}>{children}</Pill>
        </div>
      </GridRowCenter>
    </div>
  );
}

/** Several related-links pills sharing ONE row-snapped grid row — for feature
 *  pages that point sideways to 2–3 sibling pages at once. */
export function SectionCtaRow({ links }: { links: { href: string; label: string }[] }) {
  const onGrid = useRefGridMode();
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    // No negative pull-up: the pill row is a standalone floater and keeps one
    // empty grid row between itself and the band above it (GridRowCenter snaps
    // it onto the next row line past the preceding section's tail).
    <div ref={rootRef} className="relative">
      {onGrid && <GridClearing rootRef={rootRef} />}
      <GridRowCenter className="flex items-center justify-center">
        <div
          data-grid-block
          className="relative z-10 flex w-fit flex-wrap items-center justify-center gap-3"
        >
          {links.map((l) => (
            <Pill key={l.href} href={l.href}>
              {l.label}
            </Pill>
          ))}
        </div>
      </GridRowCenter>
    </div>
  );
}
